import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getProductById,
  clearCurrentProduct,
  resetCreate,
  resetUpdate,
} from "../slices/productSlice";
import { usePermissions } from "../utils/permissions";
import PermissionGate from "../componets/PermissionGate";
import BottomNavbar from "../componets/BottomNavbar";

const ProductManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const {
    products,
    pagination,
    loading,
    createLoading,
    updateLoading,
    createSuccess,
    updateSuccess,
  } = useSelector((state) => state.product);

  usePermissions();
  const { activeRoles } = useSelector((state) => state.role);
  const hasLoadedProducts = useRef(false);

  // Check permission directly from Redux state to avoid infinite loops
  const canReadProducts = useMemo(() => {
    if (user?.role === "admin") return true;
    const userRole = activeRoles.find((role) => role.name === user?.role);
    if (!userRole) return false;
    const permission = userRole.permissions.find((p) => p.resource === "products");
    return permission && permission.actions.includes("read");
  }, [user?.role, activeRoles]);

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    unit: "",
    hsnCode: "",
    gstRate: "",
    isActive: true,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [editingProduct, setEditingProduct] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (!canReadProducts) {
      navigate("/my-business");
      return;
    }

    // Only load products once when component mounts and user is authenticated
    if (!hasLoadedProducts.current) {
      dispatch(getAllProducts({ page: 1, limit: 100 }));
      hasLoadedProducts.current = true;
    }
  }, [dispatch, isAuthenticated, navigate, canReadProducts]);

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setFormData({
        name: "",
        price: "",
        unit: "",
        hsnCode: "",
        gstRate: "",
        isActive: true,
      });
      setValidationErrors({});
      setEditingProduct(null);
      dispatch(resetCreate());
      // Reload products after successful creation
      hasLoadedProducts.current = false;
      dispatch(getAllProducts({ page: 1, limit: 100, search: searchTerm }));
      hasLoadedProducts.current = true;
    }
  }, [createSuccess, dispatch, searchTerm]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setFormData({
        name: "",
        price: "",
        unit: "",
        hsnCode: "",
        gstRate: "",
        isActive: true,
      });
      setValidationErrors({});
      setEditingProduct(null);
      dispatch(resetUpdate());
      dispatch(clearCurrentProduct());
      // Reload products after successful update
      hasLoadedProducts.current = false;
      dispatch(getAllProducts({ page: 1, limit: 100, search: searchTerm }));
      hasLoadedProducts.current = true;
    }
  }, [updateSuccess, dispatch, searchTerm]);

  // eslint-disable-next-line no-unused-vars
  const _handleSearch = (e) => {
    e.preventDefault();
    dispatch(getAllProducts({ page: 1, limit: 100, search: searchTerm }));
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || "",
        price: product.price || "",
        unit: product.unit || "",
        hsnCode: product.hsnCode || "",
        gstRate: product.gstRate !== undefined && product.gstRate !== null ? product.gstRate : "",
        isActive: product.isActive !== undefined ? product.isActive : true,
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: "",
        price: "",
        unit: "",
        hsnCode: "",
        gstRate: "",
        isActive: true,
      });
    }
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      name: "",
      price: "",
      unit: "",
      hsnCode: "",
      gstRate: "",
      isActive: true,
    });
    setValidationErrors({});
    setEditingProduct(null);
    dispatch(clearCurrentProduct());
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Product name is required";
    } else if (formData.name.trim().length > 200) {
      errors.name = "Product name cannot exceed 200 characters";
    }

    if (!formData.price) {
      errors.price = "Price is required";
    } else if (isNaN(formData.price) || parseFloat(formData.price) < 0) {
      errors.price = "Price must be a valid positive number";
    }

    if (!formData.unit.trim()) {
      errors.unit = "Unit is required";
    } else if (formData.unit.trim().length > 50) {
      errors.unit = "Unit cannot exceed 50 characters";
    }

    if (!formData.hsnCode.trim()) {
      errors.hsnCode = "HSN code is required";
    } else if (formData.hsnCode.trim().length > 20) {
      errors.hsnCode = "HSN code cannot exceed 20 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const productData = {
      name: formData.name.trim(),
      price: parseFloat(formData.price),
      unit: formData.unit.trim(),
      hsnCode: formData.hsnCode.trim(),
      description: "",
      isActive: formData.isActive,
    };
    const gstVal = parseFloat(formData.gstRate);
    if (formData.gstRate !== "" && formData.gstRate !== undefined && !isNaN(gstVal) && gstVal >= 0 && gstVal <= 100) {
      productData.gstRate = gstVal;
    } else if (editingProduct && (formData.gstRate === "" || formData.gstRate === undefined)) {
      productData.gstRate = null; // clear when empty on update
    }

    if (editingProduct) {
      dispatch(
        updateProduct({ productId: editingProduct._id, productData })
      );
    } else {
      dispatch(createProduct(productData));
    }
  };

  const handleOpenDeleteModal = (productId) => {
    setDeleteProductId(productId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteProductId(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = () => {
    if (deleteProductId) {
      dispatch(deleteProduct(deleteProductId));
      setIsDeleteModalOpen(false);
      setDeleteProductId(null);
      // Reset the ref so products can be reloaded after delete
      hasLoadedProducts.current = false;
      setTimeout(() => {
        dispatch(getAllProducts({ page: 1, limit: 100, search: searchTerm }));
        hasLoadedProducts.current = true;
      }, 500);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _handleView = async (productId) => {
    await dispatch(getProductById(productId));
    const product = products.find((p) => p._id === productId);
    if (product) {
      handleOpenModal(product);
    }
  };


  if (loading && products.length === 0) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={{
        ...styles.content,
        padding: isMobile ? "16px" : "24px",
        paddingBottom: isMobile ? "100px" : "100px",
      }}>
        {/* Page Header */}
        <div style={{
          ...styles.pageHeader,
          marginBottom: isMobile ? "16px" : "24px",
        }}>
          <h1 style={{
            ...styles.pageTitle,
            fontSize: isMobile ? "28px" : "32px",
          }}>Product List</h1>
        </div>

        {/* Search Bar */}
        <div style={{
          ...styles.searchContainer,
          marginBottom: isMobile ? "16px" : "20px",
          padding: isMobile ? "8px" : "12px",
        }}>
          <div style={{ position: "relative", width: "100%" }}>
            <div style={{
              position: "absolute",
              left: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#667781",
              fontSize: "16px",
              pointerEvents: "none",
              zIndex: 1,
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                dispatch(getAllProducts({ page: 1, limit: 100, search: e.target.value }));
              }}
              style={styles.searchInput}
            />
          </div>
        </div>

        {/* Product Cards */}
        <div style={{
          ...styles.productsContainer,
          gap: isMobile ? "12px" : "16px",
        }}>
          {products.length === 0 ? (
            <div style={{
              ...styles.noProducts,
              padding: isMobile ? "60px 20px" : "80px 40px",
            }}>
              <div style={{ fontSize: "64px", marginBottom: "16px", opacity: 0.6 }}>📦</div>
              <p style={{ margin: "0 0 24px 0", fontSize: "17px", fontWeight: "400", color: "#667781" }}>
                No products found
              </p>
              <PermissionGate resource="products" action="create">
                <button
                  onClick={() => handleOpenModal()}
                  style={styles.createFirstButton}
                >
                  Create First Product
                </button>
              </PermissionGate>
            </div>
          ) : (
            products.map((product) => (
              <div 
                key={product._id} 
                style={{
                  ...styles.productCard,
                  padding: isMobile ? "16px" : "20px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                }}
              >
                <div style={styles.productCardContent}>
                  <div style={styles.productInfo}>
                    <div style={{
                      ...styles.productName,
                      fontSize: isMobile ? "16px" : "18px",
                    }}>{product.name.toUpperCase()}</div>
                    <div style={styles.productDetails}>
                      <div style={styles.productDetailRow}>
                        <span style={styles.productDetailLabel}>Price: </span>
                        <span style={styles.productDetailValue}>{product.price.toFixed(1)}</span>
                        {product.unit && (
                          <>
                            <span style={styles.separator}> | </span>
                            <span style={styles.productDetailLabel}>Unit: </span>
                            <span style={styles.productDetailValue}>{product.unit}</span>
                          </>
                        )}
                      </div>
                      {product.hsnCode && (
                        <div style={styles.productDetailRow}>
                          <span style={styles.productDetailLabel}>HSN: </span>
                          <span style={styles.productDetailValue}>{product.hsnCode}</span>
                        </div>
                      )}
                      {(product.gstRate ?? 0) > 0 && (
                        <div style={styles.productDetailRow}>
                          <span style={styles.productDetailLabel}>GST: </span>
                          <span style={styles.productDetailValue}>{product.gstRate}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={styles.productActions}>
                    <PermissionGate resource="products" action="update">
                      <button
                        onClick={() => handleOpenModal(product)}
                        style={styles.productEditButton}
                        title="Edit Product"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f0f2f5";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        ✏️
                      </button>
                    </PermissionGate>
                    <PermissionGate resource="products" action="delete">
                      <button
                        onClick={() => handleOpenDeleteModal(product._id)}
                        style={styles.productDeleteButton}
                        title="Delete Product"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff5f5";
                          e.currentTarget.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        🗑️
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div style={styles.pagination}>
            <button
              onClick={() =>
                dispatch(
                  getAllProducts({
                    page: pagination.currentPage - 1,
                    limit: 100,
                    search: searchTerm,
                  })
                )
              }
              disabled={pagination.currentPage === 1}
              style={styles.pageButton}
            >
              Previous
            </button>
            <span style={styles.pageInfo}>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() =>
                dispatch(
                  getAllProducts({
                    page: pagination.currentPage + 1,
                    limit: 100,
                    search: searchTerm,
                  })
                )
              }
              disabled={pagination.currentPage === pagination.totalPages}
              style={styles.pageButton}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Floating Action Button - Add Product - Positioned above bottom navbar */}
      <PermissionGate resource="products" action="create">
        <button
          onClick={() => handleOpenModal()}
          style={{
            ...styles.fabButton,
            bottom: isMobile ? "90px" : "100px",
            borderRadius: isMobile ? "28px" : "32px",
            padding: isMobile ? "14px 24px" : "16px 32px",
            fontSize: isMobile ? "16px" : "18px",
            zIndex: 1001,
          }}
          title="Add Product"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateX(-50%) translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 53, 69, 0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateX(-50%) translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 16px rgba(220, 53, 69, 0.4)";
          }}
        >
          <span style={{
            ...styles.fabIcon,
            fontSize: isMobile ? "20px" : "24px",
          }}>📦</span>
          <span style={styles.fabText}>Add Product</span>
        </button>
      </PermissionGate>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
            zIndex: 1002,
            paddingBottom: isMobile ? "100px" : "100px",
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              ...styles.modalContent,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "20px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "800px",
              width: isMobile ? "100%" : "90%",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
              marginBottom: isMobile ? "100px" : "0",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              ...styles.modalHeader,
              marginBottom: isMobile ? "16px" : "24px",
            }}>
              <h3 style={{
                ...styles.modalTitle,
                fontSize: isMobile ? "20px" : "24px",
              }}>
                {editingProduct ? "Edit Item Details" : "Add Item Details"}
              </h3>
              <button 
                onClick={handleCloseModal} 
                style={styles.closeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f2f5";
                  e.currentTarget.style.color = "#111b21";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#667781";
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{
              ...styles.form,
              gap: isMobile ? "16px" : "20px",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
            }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Product Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.name ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter product name"
                />
                {validationErrors.name && (
                  <span style={styles.error}>{validationErrors.name}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Price <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.price ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter price"
                />
                {validationErrors.price && (
                  <span style={styles.error}>{validationErrors.price}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Unit <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) =>
                    setFormData({ ...formData, unit: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.unit ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="e.g., KG, PCS, LITER"
                />
                {validationErrors.unit && (
                  <span style={styles.error}>{validationErrors.unit}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  HSN Code <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.hsnCode}
                  onChange={(e) =>
                    setFormData({ ...formData, hsnCode: e.target.value })
                  }
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.hsnCode ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Enter HSN code"
                />
                {validationErrors.hsnCode && (
                  <span style={styles.error}>{validationErrors.hsnCode}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>GST % (default in bill)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.gstRate}
                  onChange={(e) => {
                    const val = e.target.value;
                    const v = parseFloat(val);
                    setFormData({ ...formData, gstRate: val === "" ? "" : (isNaN(v) ? "" : v) });
                  }}
                  style={{
                    ...styles.input,
                    borderColor: validationErrors.gstRate ? "#dc3545" : "#e9edef",
                    width: "100%",
                  }}
                  placeholder="Optional (e.g. 5, 12, 18)"
                />
                <span style={{ fontSize: 12, color: "#667781" }}>Used as default when adding this product to a bill. Can be changed at billing time.</span>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    style={styles.checkbox}
                  />
                  Active
                </label>
              </div>

              <div style={{
                ...styles.formActions,
                marginTop: isMobile ? "8px" : "16px",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    ...styles.cancelButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "100px",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || updateLoading}
                  style={{
                    ...styles.submitButton,
                    padding: isMobile ? "12px 20px" : "12px 24px",
                    flex: isMobile ? "1" : "none",
                    minWidth: isMobile ? "0" : "140px",
                  }}
                >
                  {createLoading || updateLoading
                    ? "Saving..."
                    : editingProduct
                    ? "Update Item"
                    : "Save Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div 
          style={{
            ...styles.modalOverlay,
            alignItems: isMobile ? "flex-start" : "center",
          }} 
          onClick={handleCloseDeleteModal}
        >
          <div 
            style={{
              ...styles.deleteModalContent,
              borderRadius: isMobile ? "0" : "8px",
              padding: isMobile ? "24px 16px" : "30px",
              maxWidth: isMobile ? "100%" : "400px",
              width: isMobile ? "100%" : "90%",
              marginBottom: isMobile ? "100px" : "0",
              maxHeight: isMobile ? "calc(100vh - 100px)" : "calc(90vh - 100px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              ...styles.deleteModalTitle,
              fontSize: isMobile ? "18px" : "20px",
            }}>Confirm Delete</h3>
            <p style={styles.deleteModalText}>
              Are you sure you want to delete this product? This action cannot
              be undone.
            </p>
            <div style={{
              ...styles.deleteModalActions,
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}>
              <button
                onClick={handleCloseDeleteModal}
                style={{
                  ...styles.cancelButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete} 
                style={{
                  ...styles.deleteConfirmButton,
                  padding: isMobile ? "12px 20px" : "12px 24px",
                  flex: isMobile ? "1" : "none",
                  minWidth: isMobile ? "0" : "100px",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <BottomNavbar />
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    display: "flex",
    flexDirection: "column",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
    paddingBottom: "80px",
  },
  content: {
    maxWidth: "1200px",
    padding: "24px",
    minHeight: "calc(100vh - 80px)",
  },
  pageHeader: {
    marginBottom: "24px",
    paddingBottom: "16px",
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.02em",
  },
  searchContainer: {
    marginBottom: "20px",
    padding: "12px",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px 12px 44px",
    border: "1px solid #e9edef",
    borderRadius: "24px",
    backgroundColor: "#ffffff",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    outline: "none",
    transition: "border-color 0.2s",
    letterSpacing: "-0.01em",
  },
  productsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "20px",
  },
  productCard: {
    backgroundColor: "#ffeef2",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #ffd6e0",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  productCardContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#111b21",
    marginBottom: "8px",
    letterSpacing: "-0.01em",
    lineHeight: "22px",
    textTransform: "uppercase",
  },
  productDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  productDetailRow: {
    fontSize: "14px",
    fontWeight: "400",
    color: "#667781",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  productDetailLabel: {
    fontWeight: "500",
    color: "#667781",
  },
  productDetailValue: {
    color: "#111b21",
  },
  separator: {
    color: "#667781",
    margin: "0 4px",
  },
  productActions: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
  },
  productEditButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  productDeleteButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    border: "1px solid #e9edef",
    cursor: "pointer",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, transform 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  noProducts: {
    padding: "80px 40px",
    textAlign: "center",
    color: "#667781",
  },
  createFirstButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
    boxShadow: "0 2px 8px rgba(220, 53, 69, 0.3)",
  },
  fabButton: {
    position: "fixed",
    bottom: "100px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "32px",
    padding: "16px 32px",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 4px 16px rgba(220, 53, 69, 0.4)",
    zIndex: 1001,
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  fabIcon: {
    fontSize: "24px",
    lineHeight: "1",
  },
  fabText: {
    letterSpacing: "-0.01em",
    whiteSpace: "nowrap",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginTop: "20px",
  },
  pageButton: {
    padding: "8px 16px",
    backgroundColor: "#007bff",
    color: "#ffffff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
  },
  pageInfo: {
    fontSize: "16px",
    color: "#333",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    display: "flex",
    justifyContent: "center",
    zIndex: 1002,
    overflowY: "auto",
    paddingBottom: "100px",
  },
  modalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: "30px",
    maxWidth: "800px",
    width: "90%",
    maxHeight: "calc(90vh - 100px)",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    marginBottom: "100px",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e9edef",
  },
  modalTitle: {
    fontSize: "24px",
    fontWeight: "500",
    color: "#111b21",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  closeButton: {
    backgroundColor: "transparent",
    border: "none",
    fontSize: "32px",
    cursor: "pointer",
    color: "#667781",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s, color 0.2s",
    lineHeight: "1",
    fontWeight: "300",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  input: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
  },
  textarea: {
    padding: "12px 16px",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    fontSize: "15px",
    fontFamily: "inherit",
    resize: "vertical",
    fontWeight: "400",
    color: "#111b21",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
  },
  error: {
    color: "#dc3545",
    fontSize: "12px",
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
    borderTop: "1px solid #e9edef",
    marginTop: "auto",
    position: "sticky",
    bottom: 0,
    backgroundColor: "#ffffff",
    zIndex: 1,
  },
  cancelButton: {
    padding: "12px 24px",
    backgroundColor: "#f0f2f5",
    color: "#111b21",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
  },
  submitButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "140px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  deleteModalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: "30px",
    maxWidth: "400px",
    width: "90%",
    maxHeight: "calc(90vh - 100px)",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    marginBottom: "100px",
  },
  deleteModalTitle: {
    fontSize: "20px",
    fontWeight: "500",
    color: "#111b21",
    marginBottom: "12px",
    letterSpacing: "-0.01em",
  },
  deleteModalText: {
    fontSize: "14px",
    color: "#667781",
    marginBottom: "24px",
    lineHeight: "20px",
    fontWeight: "400",
  },
  deleteModalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    paddingBottom: "20px",
  },
  deleteConfirmButton: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "400",
    transition: "background-color 0.2s",
    minWidth: "100px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
};

export default ProductManagement;

